/**
 * UserEditPage — Full-page 3-step user wizard.
 * Routes:  /app/users/new
 *          /app/users/:userId/edit
 *
 * Step 1: Account details (username, email, password, role, org)
 * Step 2: Machine access + page modules
 * Step 3: Per-machine channel visibility (kanban DnD via UserChannelBoard)
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Stepper, Button, Group, TextInput, PasswordInput, Select,
  Checkbox, Stack, Text, Loader, Alert, Divider,
  Badge, SimpleGrid, Image, Anchor, Breadcrumbs, ActionIcon,
  Accordion,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { MdArrowBack, MdPrecisionManufacturing } from 'react-icons/md';
import {
  createUser, updateUser, getUser,
  getMachines, getUserMachines,
  getUserChannelConfig, saveUserChannelConfig,
  getAvailableChannels,
  getUserModules, saveUserModules,
  getOrgs,
} from '../../API/adminAPI';
import UserChannelBoard from './UserChannelBoard';
import AtomsLogo from '../../assets/AtomsLogo.png';

const ATOMS_BLUE = 'var(--accent)';

const BASE_ROLES = [
  { value: 'user',              label: 'User' },
  { value: 'distributor_admin', label: 'Distributor Admin' },
  { value: 'oem_admin',         label: 'OEM Admin' },
];

const SUPER_ROLES = [
  ...BASE_ROLES,
  { value: 'atoms_admin', label: 'ATOMS Admin' },
];

const STEPS = [
  { label: 'Details',  description: 'Account info' },
  { label: 'Access',   description: 'Machines & modules' },
  { label: 'Channels', description: 'Visibility config' },
];

// ── Page ─────────────────────────────────────────────────────────────────────

const UserEditPage = () => {
  const { userId } = useParams();
  const navigate   = useNavigate();
  const location   = useLocation();
  const role       = localStorage.getItem('role') || 'user';
  const isNew      = !userId;

  const backPath = role === 'atoms_admin' ? '/app/super-admin' : '/app/admin-panel';

  const [pageLoading, setPageLoading] = useState(true);
  const [pageError,   setPageError]   = useState('');
  const [loadedUser,  setLoadedUser]  = useState(location.state?.user || null);
  const [orgs,        setOrgs]        = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const tasks = [];
        if (!loadedUser && userId) {
          tasks.push(
            getUser(userId).then((data) => setLoadedUser(data))
          );
        }
        if (role === 'atoms_admin') {
          tasks.push(
            getOrgs().then((d) =>
              setOrgs(
                (d.organisations || []).map((o) => ({
                  value: String(o.id),
                  label: `${o.name} (${o.org_type})`,
                }))
              )
            )
          );
        }
        await Promise.all(tasks);
      } catch (e) {
        setPageError(e?.response?.data?.error || 'Failed to load user data');
      } finally {
        setPageLoading(false);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (pageLoading) return (
    <PageShell backPath={backPath} crumb={isNew ? 'New User' : 'Loading…'}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <Loader size="lg" />
      </div>
    </PageShell>
  );

  if (pageError) return (
    <PageShell backPath={backPath} crumb="Error">
      <Alert color="red" m="xl">{pageError}</Alert>
    </PageShell>
  );

  return (
    <WizardContent
      user={loadedUser}
      orgs={orgs}
      isAtoms={role === 'atoms_admin'}
      backPath={backPath}
      onDone={(saved) => navigate(backPath, { state: { refresh: saved, tab: 'users' } })}
    />
  );
};

// ── WizardContent ─────────────────────────────────────────────────────────────

const WizardContent = ({ user, orgs, isAtoms, backPath, onDone }) => {
  const isEdit = Boolean(user);

  const [active,       setActive]       = useState(0);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');
  const [savedUserId,  setSavedUserId]  = useState(user?.id || null);

  // Step 2
  const [allMachines,     setAllMachines]     = useState([]);
  const [assignedIds,     setAssignedIds]     = useState(new Set());
  const [allModules,      setAllModules]      = useState([]);
  const [assignedModules, setAssignedModules] = useState([]);
  const [machinesLoading, setMachinesLoading] = useState(false);

  // Step 3
  const [configs,        setConfigs]        = useState({});
  const [configsLoading, setConfigsLoading] = useState(false);

  // Step 1 form
  const form = useForm({
    initialValues: {
      username: user?.username || '',
      email:    user?.email    || '',
      password: '',
      role:     user?.role     || 'user',
      org_id:   user?.org_id   ? String(user.org_id) : null,
    },
    validate: {
      username: (v) => v.trim() ? null : 'Username required',
      password: (v) => (!isEdit && !v.trim()) ? 'Password required for new user' : null,
      role:     (v) => v ? null : 'Select a role',
      ...(isAtoms && { org_id: (v) => v ? null : 'Select an organisation' }),
    },
  });

  // Load machines + modules on mount
  useEffect(() => {
    setMachinesLoading(true);
    const tasks = [
      getMachines().then((d) => setAllMachines(d.machines || [])),
      getUserModules(isEdit ? (user?.id || 0) : 0).then((d) => {
        setAllModules(d.all_modules || [
          { page_id: 1, display_name: 'Dashboard' },
          { page_id: 2, display_name: 'Machine' },
          { page_id: 3, display_name: 'Trail' },
          { page_id: 4, display_name: 'Report' },
        ]);
        if (isEdit) setAssignedModules(d.assigned || []);
      }),
    ];
    if (isEdit && user?.id) {
      tasks.push(
        getUserMachines(user.id).then((d) => {
          setAssignedIds(new Set((d.machines || []).map((m) => m.id)));
        })
      );
    }
    Promise.all(tasks).catch(() => {}).finally(() => setMachinesLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConfigs = async (uid) => {
    setConfigsLoading(true);
    const assigned = allMachines.filter((m) => assignedIds.has(m.id));
    const entries = await Promise.all(
      assigned.map(async (m) => {
        try {
          const [cfgData, avData] = await Promise.all([
            getUserChannelConfig(uid, m.id),
            getAvailableChannels(m.id),
          ]);
          return [m.id, {
            available:          avData.channels?.map((c) => c.internal_name) || [],
            visible_channels:   cfgData.visible_channels   || [],
            dashboard_channels: cfgData.dashboard_channels || [],
            kpi_channels:       cfgData.kpi_channels       || [],
          }];
        } catch {
          return [m.id, { available: [], visible_channels: [], dashboard_channels: [], kpi_channels: [] }];
        }
      })
    );
    setConfigs(Object.fromEntries(entries));
    setConfigsLoading(false);
  };

  const setMachineConfig = (machPk, field, value) =>
    setConfigs((prev) => ({ ...prev, [machPk]: { ...prev[machPk], [field]: value } }));

  // Navigation
  const nextStep = async () => {
    setError('');

    if (active === 0) {
      const v = form.validate();
      if (v.hasErrors) return;
      setSaving(true);
      try {
        const payload = {
          username: form.values.username.trim(),
          email:    form.values.email.trim(),
          role:     form.values.role,
          ...(isAtoms && form.values.org_id && { org_id: parseInt(form.values.org_id) }),
        };
        if (form.values.password) payload.password = form.values.password;
        let uid = savedUserId;
        if (isEdit) {
          await updateUser(savedUserId, payload);
        } else {
          const resp = await createUser(payload);
          uid = resp.user?.id || resp.id;
          setSavedUserId(uid);
        }
        setActive(1);
      } catch (e) { setError(e?.response?.data?.error || 'Save failed'); }
      finally { setSaving(false); }
      return;
    }

    if (active === 1) {
      setSaving(true);
      try {
        await saveUserModules(savedUserId, assignedModules);
        await updateUser(savedUserId, { machines: [...assignedIds] });
        await loadConfigs(savedUserId);
        setActive(2);
      } catch (e) { setError(e?.response?.data?.error || 'Machine assignment failed'); }
      finally { setSaving(false); }
      return;
    }

    if (active === 2) {
      setSaving(true);
      try {
        await Promise.all(
          Object.entries(configs).map(([machPkStr, cfg]) =>
            saveUserChannelConfig(savedUserId, parseInt(machPkStr, 10), {
              visible_channels:   cfg.visible_channels,
              dashboard_channels: cfg.dashboard_channels,
              kpi_channels:       cfg.kpi_channels,
            })
          )
        );
        onDone(true);
      } catch (e) { setError(e?.response?.data?.error || 'Channel config save failed'); }
      finally { setSaving(false); }
    }
  };

  const prevStep = () => setActive((a) => Math.max(0, a - 1));

  return (
    <PageShell
      backPath={backPath}
      crumb={isEdit ? `Edit — ${user.username}` : 'New User'}
      stepper={
        <Stepper active={active} orientation="vertical" size="sm"
          styles={{
            step:            { padding: '8px 0' },
            stepLabel:       { fontWeight: 600, fontSize: 13 },
            stepDescription: { fontSize: 11 },
          }}>
          {STEPS.map((s) => <Stepper.Step key={s.label} label={s.label} description={s.description} />)}
        </Stepper>
      }
      footer={
        <Group position="apart" style={{ width: '100%' }}>
          <Button variant="subtle" color="gray" onClick={() => onDone(false)}>Cancel</Button>
          <Group spacing="sm">
            {active > 0 && (
              <Button variant="default" onClick={prevStep} disabled={saving}>← Back</Button>
            )}
            <Button
              onClick={nextStep}
              loading={saving}
              styles={{ root: { backgroundColor: ATOMS_BLUE, '&:hover': { backgroundColor: 'var(--accent-hover)' } } }}
            >
              {active === 2 ? 'Save & Close' : 'Next →'}
            </Button>
          </Group>
        </Group>
      }
    >
      {error && <Alert color="red" mb="lg" radius="md">{error}</Alert>}

      {/* ═══ STEP 1 — DETAILS ════════════════════════════════════════════════ */}
      {active === 0 && (
        <Stack maw={480}>
          <TextInput
            label="Username"
            placeholder="john_doe"
            required
            {...form.getInputProps('username')}
          />
          <TextInput
            label="Email"
            placeholder="user@example.com"
            {...form.getInputProps('email')}
          />
          <PasswordInput
            label={isEdit ? 'New Password (leave blank to keep)' : 'Password'}
            placeholder="••••••••"
            required={!isEdit}
            {...form.getInputProps('password')}
          />
          <Select
            label="Role"
            data={isAtoms ? SUPER_ROLES : BASE_ROLES}
            required
            {...form.getInputProps('role')}
          />
          {isAtoms && orgs && (
            <Select
              label="Organisation"
              placeholder="Select organisation…"
              data={orgs}
              searchable
              required
              {...form.getInputProps('org_id')}
            />
          )}
        </Stack>
      )}

      {/* ═══ STEP 2 — ACCESS ═════════════════════════════════════════════════ */}
      {active === 1 && (
        <Stack maw={640}>
          {/* Page Modules */}
          {allModules.length > 0 && (
            <>
              <div>
                <Text size="xs" weight={700} color="dimmed" tt="uppercase" mb={10}>
                  Page Access
                </Text>
                <div style={{
                  background: '#fff',
                  border: '1px solid #e8ecf0',
                  borderRadius: 10,
                  padding: '14px 16px',
                }}>
                  <SimpleGrid cols={4} spacing="sm">
                    {allModules.map((m) => (
                      <Checkbox
                        key={m.page_id}
                        label={m.display_name}
                        checked={assignedModules.includes(m.page_id)}
                        onChange={() => setAssignedModules((prev) =>
                          prev.includes(m.page_id)
                            ? prev.filter((id) => id !== m.page_id)
                            : [...prev, m.page_id]
                        )}
                      />
                    ))}
                  </SimpleGrid>
                </div>
              </div>
              <Divider />
            </>
          )}

          {/* Machine Access */}
          <div>
            <Text size="xs" weight={700} color="dimmed" tt="uppercase" mb={10}>
              Machine Access
            </Text>
            {machinesLoading ? <Loader size="sm" /> : allMachines.length === 0
              ? <Text color="dimmed" size="sm">No machines available in your scope.</Text>
              : (
                <div style={{
                  background: '#fff',
                  border: '1px solid #e8ecf0',
                  borderRadius: 10,
                  padding: '8px',
                  maxHeight: 400,
                  overflowY: 'auto',
                }}>
                  {allMachines.map((m, idx) => {
                    const checked = assignedIds.has(m.id);
                    return (
                      <div
                        key={m.id}
                        onClick={() => {
                          const next = new Set(assignedIds);
                          if (next.has(m.id)) next.delete(m.id); else next.add(m.id);
                          setAssignedIds(next);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '10px 10px',
                          borderRadius: 7,
                          cursor: 'pointer',
                          background: checked ? '#eff6ff' : 'transparent',
                          borderBottom: idx < allMachines.length - 1 ? '1px solid #f3f4f6' : 'none',
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = '#f8fafc'; }}
                        onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = 'transparent'; }}
                      >
                        {/* Checkbox indicator */}
                        <div style={{
                          width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                          background: checked ? '#2563eb' : '#fff',
                          border: `2px solid ${checked ? '#2563eb' : '#d1d5db'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.1s',
                        }}>
                          {checked && <span style={{ color: '#fff', fontSize: 11, lineHeight: 1, fontWeight: 700 }}>✓</span>}
                        </div>

                        <MdPrecisionManufacturing size={15} style={{ color: checked ? '#2563eb' : '#9ca3af', flexShrink: 0 }} />

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" weight={500} color={checked ? '#1d4ed8' : '#374151'} lineClamp={1}>
                            {m.machine_name || m.machine_id}
                          </Text>
                          <Text size="xs" color="dimmed">{m.machine_id}</Text>
                        </div>

                        {m.owned_by && (
                          <Badge size="xs" variant="light" color="blue">{m.owned_by}</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            }
          </div>

          {assignedIds.size > 0 && (
            <Text size="xs" color="dimmed">
              {assignedIds.size} machine{assignedIds.size !== 1 ? 's' : ''} selected. Click Next to configure channel visibility for each machine.
            </Text>
          )}
        </Stack>
      )}

      {/* ═══ STEP 3 — CHANNELS ════════════════════════════════════════════════ */}
      {active === 2 && (
        <Stack>
          <Text size="sm" color="dimmed" maw={720}>
            Configure which channels each machine shows to this user. Drag channels from the pool into
            sections. <strong>Visible</strong> = shown in status/reports.
            <strong> Dashboard</strong> = metric pills (max 3).
            <strong> KPI</strong> = hourly charts. Leave all empty to inherit machine defaults.
          </Text>

          {configsLoading ? (
            <Loader />
          ) : Object.keys(configs).length === 0 ? (
            <Text color="dimmed">No machines assigned — skip this step.</Text>
          ) : (
            <Accordion chevronPosition="left" variant="separated">
              {allMachines.filter((m) => configs[m.id]).map((m) => {
                const cfg = configs[m.id];
                const totalConfigured = cfg.visible_channels.length +
                  cfg.dashboard_channels.length + cfg.kpi_channels.length;
                return (
                  <Accordion.Item
                    key={m.id}
                    value={String(m.id)}
                    style={{ background: '#fff', borderRadius: 10, border: '1px solid #e8ecf0' }}
                  >
                    <Accordion.Control style={{ borderRadius: 10 }}>
                      <Group spacing={8}>
                        <MdPrecisionManufacturing size={16} style={{ color: '#6b7280' }} />
                        <Text weight={600} size="sm">{m.machine_name || m.machine_id}</Text>
                        <Badge size="xs" color="gray" variant="outline">{m.machine_id}</Badge>
                        {totalConfigured > 0 && (
                          <Badge size="xs" color="blue" variant="light">
                            {totalConfigured} configured
                          </Badge>
                        )}
                        {totalConfigured === 0 && (
                          <Badge size="xs" color="gray" variant="light">all defaults</Badge>
                        )}
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <UserChannelBoard
                        available={cfg.available}
                        visibleChannels={cfg.visible_channels}
                        dashboardChannels={cfg.dashboard_channels}
                        kpiChannels={cfg.kpi_channels}
                        onChange={(field, value) => setMachineConfig(m.id, field, value)}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </Stack>
      )}
    </PageShell>
  );
};

// ── PageShell ─────────────────────────────────────────────────────────────────

const PageShell = ({ backPath, crumb, stepper, footer, children }) => {
  const navigate = useNavigate();
  const role     = localStorage.getItem('role') || 'user';
  const panelLabel = role === 'atoms_admin' ? 'Super Admin' : 'Admin Panel';

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f4f6f8' }}>

      {/* Header */}
      <div style={{
        background: ATOMS_BLUE, color: 'white', height: 56,
        display: 'flex', alignItems: 'center', padding: '0 24px',
        flexShrink: 0, gap: 16,
      }}>
        <ActionIcon
          variant="transparent"
          onClick={() => navigate(backPath)}
          title="Back"
          sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white', background: 'rgba(255,255,255,0.1)' } }}
        >
          <MdArrowBack size={20} />
        </ActionIcon>
        <Image src={AtomsLogo} width={28} height={28} fit="contain" style={{ flexShrink: 0 }} />
        <Breadcrumbs separator="›"
          styles={{ separator: { color: 'rgba(255,255,255,0.4)', margin: '0 6px' } }}>
          <Anchor size="sm" onClick={() => navigate(backPath)}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' }, cursor: 'pointer' }}>
            {panelLabel}
          </Anchor>
          <Text size="sm" sx={{ color: 'white', fontWeight: 600 }}>{crumb}</Text>
        </Breadcrumbs>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Sidebar */}
        {stepper && (
          <div style={{
            width: 220, background: 'white',
            borderRight: '1px solid #dee2e6',
            padding: '32px 20px', flexShrink: 0, overflowY: 'auto',
          }}>
            {stepper}
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '40px 48px' }}>
          {children}
        </div>
      </div>

      {/* Footer */}
      {footer && (
        <div style={{
          background: 'white', borderTop: '1px solid #dee2e6',
          padding: '0 48px', height: 64,
          display: 'flex', alignItems: 'center', flexShrink: 0,
        }}>
          {footer}
        </div>
      )}
    </div>
  );
};

export default UserEditPage;
